import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Container } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { propuestasApi } from "src/services/propuestasApi";
import { derivarDecisionesPorItem } from "src/utils/propuestasV2/decisionesRenegociacionV2";
import ProposalLoadingV2 from "./ProposalLoadingV2";
import ErrorViewV2 from "./ErrorViewV2";
import ProposalHeaderV2 from "./ProposalHeaderV2";
import TerminalStateBanner from "./TerminalStateBanner";
import RenegociacionNotice from "./RenegociacionNotice";
import ProposalSectionsV2 from "./ProposalSectionsV2";
import StickyActionBar from "./StickyActionBar";
import OTPDialog from "./OTPDialog";

// Terminal de verdad: el desenlace de la propuesta ya está escrito y no
// vuelve. Es la lista que autoriza el banner de desenlace: un enlace
// vencido también lo hace -traduciendo el estado a caducada antes de
// pintarlo-, salvo sobre una conversación abierta, que no terminó.
// Agregarle un estado que todavía puede cambiar (como EN_RENEGOCIACION) le
// haría anunciar un cierre que no ocurrió.
const ESTADOS_TERMINALES = [
  "ACEPTADA",
  "RECHAZADA",
  "PARCIALMENTE_ACEPTADA",
  "CADUCADA",
];

// No accionable ahora: el cliente no puede decidir en este momento, sin que
// eso implique que la propuesta terminó. Suma EN_RENEGOCIACION a los
// terminales porque el backend exige PENDIENTE para aceptar o rechazar, así
// que mostrarle botones aquí solo produciría un rechazo del servidor. Esta
// es la lista que apaga la barra de acciones y pone las secciones en solo
// lectura.
const ESTADOS_SIN_ACCION_DISPONIBLE = [
  ...ESTADOS_TERMINALES,
  "EN_RENEGOCIACION",
];

// Altura reservada al pie de la página para que el `StickyActionBar` no tape
// contenido. Aproximación holgada: barra + padding + safe-area mobile.
const PADDING_FOOTER = 140;

/**
 * Contenedor raíz del portal público V2 (`/propuesta/v2/ver?token=...`).
 *
 * Responsabilidades:
 *  - Leer `token` del query param y ejecutar `useGetPropuestaV2PublicQuery`.
 *  - Mantener el estado local de `decisions` (el GET público trae respuestas
 *    previas embebidas en `item.respuesta` si la propuesta ya fue respondida;
 *    `decisions` local solo se usa en modo edición).
 *  - Decidir, por separado, si el cliente puede accionar ahora mismo
 *    (`estado` fuera de la lista amplia y `tokenExpired` falso) y si el
 *    desenlace ya está escrito (`estado` en la lista angosta de terminales,
 *    o enlace vencido sobre una propuesta que sí estaba esperándolo a él),
 *    porque no toda pantalla sin acciones es una pantalla terminal.
 *  - Derivar, con `derivarDecisionesPorItem`, qué ítems ya quedaron
 *    ACEPTADOS en una ronda previa y excluirlos de `itemsEnMesa`: esa es la
 *    lista que se ofrece a decisión y la que cuenta el sticky bar. La lista
 *    renderizada y los totales económicos siguen viendo `data.items`
 *    completo, porque un ítem aceptado sigue siendo parte de la propuesta y
 *    de su precio.
 *  - Orquestar: header + banner de desenlace o aviso de renegociación +
 *    secciones narrativas + sticky bar (solo si el cliente puede accionar)
 *    + OTP dialog.
 *  - Manejar el rechazo masivo seteando `decisions` a RECHAZADO solo sobre
 *    `itemsEnMesa` antes de abrir el OTP.
 *  - Manejar la descarga del PDF firmado post-firma: abre `urlPropuesta` en
 *    nueva pestaña (el PDF firmado con QR vive en Dropbox como snapshot
 *    inmutable).
 */
const ProposalViewV2 = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, error, refetch } =
    propuestasApi.useGetPropuestaV2PublicQuery({ token }, { skip: !token });

  const [decisions, setDecisions] = useState({});
  const [otpOpen, setOtpOpen] = useState(false);

  const noAccionable = useMemo(() => {
    if (!data) return false;
    return (
      ESTADOS_SIN_ACCION_DISPONIBLE.includes(data.propuesta.estado) ||
      !!data.tokenExpired
    );
  }, [data]);

  // Aviso propio de EN_RENEGOCIACION: se activa por el estado real, nunca
  // por descarte entre las dos listas de arriba. Restar ("está en la lista
  // amplia y no en la angosta") da hoy el mismo resultado porque
  // EN_RENEGOCIACION es el único estado exclusivo de la lista amplia, pero
  // el próximo estado que entre solo ahí heredaría este mensaje -uno que le
  // diría al cliente que revisamos algo que no pidió- sin que nadie lo haya
  // decidido. Tampoco mira el vencimiento del enlace: el cliente conserva a
  // propósito el enlace de una conversación abierta para confirmar que su
  // solicitud llegó, el plazo interno es más corto que la vigencia del
  // enlace y una reactivación por inactividad interna lo conserva en vez de
  // renovarlo, así que entra con el enlace vencido y este aviso es lo único
  // que le explica por qué todavía no puede decidir.
  const enRenegociacion = data?.propuesta?.estado === "EN_RENEGOCIACION";

  // El vencimiento del enlace escribe el desenlace de la propuesta: una que
  // caducó esperando al cliente muestra el banner de caducada aunque su
  // estado guardado siga siendo PENDIENTE. La conversación abierta es la
  // excepción, porque no terminó: la casa le debe una respuesta y vuelve
  // con una ronda nueva, así que anunciarle un cierre sería falso.
  // Se excluye ese caso en vez de enumerar los estados que sí caducan para
  // que un estado nuevo herede el banner y no el silencio: sin banner y sin
  // aviso, el cliente queda en solo lectura sin ninguna explicación.
  // Esto no apaga las acciones -de eso se encarga `noAccionable`, que sigue
  // mirando el vencimiento tal cual, porque con el enlace vencido el
  // backend le rechazaría cualquier decisión-: son dos conceptos y solo
  // este distingue por estado.
  //
  // Va después de `enRenegociacion` y lo necesita declarado: al revés, el
  // cortocircuito del `&&` esconde la falla con el enlace vigente y la deja
  // explotar solo con el vencido, que es exactamente el caso que esta
  // condición existe para atender -- se vería bien en una prueba local y
  // reventaría en pantalla para quien la sufre.
  const enlaceVencidoDeclaraDesenlace =
    !!data?.tokenExpired && !enRenegociacion;

  const esTerminal = useMemo(() => {
    if (!data) return false;
    return (
      ESTADOS_TERMINALES.includes(data.propuesta.estado) ||
      enlaceVencidoDeclaraDesenlace
    );
  }, [data, enlaceVencidoDeclaraDesenlace]);

  // Decisión vigente por ítem (ACEPTADO/RECHAZADO/OBSERVADO), la misma que
  // usa el backend para decidir qué ítems admite en el envío de una ronda.
  const decisionesVigentes = useMemo(
    () => derivarDecisionesPorItem(data?.items),
    [data],
  );

  // Ítems que siguen en la mesa: todos los vigentes menos los ya ACEPTADOS.
  // Un ítem aceptado ya se materializó (el plan o la tarea se creó) y el
  // backend lo rechaza si llega de nuevo en el envío, así que ni se ofrece
  // como decisión ni se cuenta como pendiente. RECHAZADO y OBSERVADO no son
  // terminales por ítem -vuelven a la mesa en cada ronda nueva- y quedan
  // dentro de esta lista igual que un ítem sin respuesta.
  const itemsEnMesa = useMemo(
    () =>
      (data?.items ?? []).filter(
        (it) => decisionesVigentes.get(it.id) !== "ACEPTADO",
      ),
    [data, decisionesVigentes],
  );

  // Porcentaje de items aceptados sobre los que tienen respuesta. Se calcula
  // solo para alimentar el banner terminal cuando el estado es
  // PARCIALMENTE_ACEPTADA; ACEPTADA hardcodea 100% y los otros estados no
  // muestran porcentaje.
  const porcentajeAceptado = useMemo(() => {
    if (!data?.items?.length) return null;
    const conRespuesta = data.items.filter((it) => it.respuesta);
    if (conRespuesta.length === 0) return null;
    const aceptados = conRespuesta.filter(
      (it) => it.respuesta.decision === "ACEPTADO",
    ).length;
    return Math.round((aceptados / conRespuesta.length) * 100);
  }, [data]);

  const fechaTerminal = useMemo(() => {
    if (!data) return null;
    const itemConRespuesta = (data.items ?? []).find(
      (it) => it.respuesta?.fechaDecision,
    );
    if (itemConRespuesta) return itemConRespuesta.respuesta.fechaDecision;
    if (data.propuesta?.exp) {
      return new Date(data.propuesta.exp * 1000).toISOString();
    }
    return data.propuesta?.fechaEnvioCorreo || null;
  }, [data]);

  const respuestas = useMemo(
    () =>
      Object.entries(decisions)
        .filter(([, d]) => d.decision)
        .map(([itemId, d]) => ({
          item_id: Number(itemId),
          decision: d.decision,
          comentario_cliente:
            d.decision === "RECHAZADO" ? d.motivo || null : null,
        })),
    [decisions],
  );

  // ── Early returns ────────────────────────────────────────────────
  if (!token) {
    return (
      <ErrorViewV2
        error={{
          status: 401,
          data: { message: "Enlace inválido o incompleto." },
        }}
      />
    );
  }
  if (isLoading) return <ProposalLoadingV2 />;
  if (error) return <ErrorViewV2 error={error} onRetry={refetch} />;
  if (!data) return <ProposalLoadingV2 />;

  // ── Handlers ─────────────────────────────────────────────────────
  const handleChangeDecision = (itemId, value) => {
    setDecisions((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleSubmit = () => setOtpOpen(true);

  const handleRejectAll = (motivoComun) => {
    // Setea a RECHAZADO solo los ítems que siguen en la mesa -nunca los ya
    // ACEPTADOS-, luego abre el OTP en el siguiente tick. Usar
    // `setTimeout(..., 0)` asegura que el batch de `setDecisions` se procese
    // y `respuestas` se recalcule antes de que el OTPDialog monte con las
    // props actualizadas.
    const allRejected = {};
    for (const it of itemsEnMesa) {
      allRejected[it.id] = { decision: "RECHAZADO", motivo: motivoComun };
    }
    setDecisions(allRejected);
    setTimeout(() => setOtpOpen(true), 0);
  };

  const handleOTPSuccess = () => {
    setOtpOpen(false);
    enqueueSnackbar("Respuesta registrada correctamente.", {
      variant: "success",
    });
    refetch();
  };

  const handleDownloadPDF = () => {
    if (data.propuesta.urlPropuesta) {
      window.open(data.propuesta.urlPropuesta, "_blank", "noopener,noreferrer");
    }
  };

  // Si el enlace venció y con eso quedó escrito el desenlace pero el estado
  // real no es terminal (propuesta en PENDIENTE que caducó), el banner debe
  // mostrar CADUCADA aunque el estado de la propuesta siga siendo
  // PENDIENTE. Sobre una conversación abierta el vencimiento no declara
  // nada, y ahí este banner ni siquiera se pinta.
  const estadoBanner = enlaceVencidoDeclaraDesenlace
    ? "CADUCADA"
    : data.propuesta.estado;

  // Si algún ítem vigente ya quedó ACEPTADO, la mesa de esta ronda es más
  // chica que la propuesta completa. StickyActionBar ya no ve la lista
  // completa, así que necesita este dato para saber si sus textos de
  // rechazo masivo deben hablar de "toda la propuesta" o solo de "lo
  // pendiente".
  const hayServiciosCerrados = itemsEnMesa.length < data.items.length;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F5F5F7",
        pb: noAccionable ? 0 : `${PADDING_FOOTER}px`,
      }}
    >
      <ProposalHeaderV2
        propuesta={data.propuesta}
        onDownloadPDF={handleDownloadPDF}
      />

      {esTerminal && (
        <TerminalStateBanner
          estado={estadoBanner}
          fecha={fechaTerminal}
          porcentajeAceptado={porcentajeAceptado}
        />
      )}

      {enRenegociacion && <RenegociacionNotice />}

      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
        <ProposalSectionsV2
          propuesta={data.propuesta}
          solicitante={data.solicitante}
          vendedor={data.vendedor}
          items={data.items}
          empresas={data.empresas}
          equipo={data.equipo}
          decisions={decisions}
          onChangeDecision={handleChangeDecision}
          readOnly={noAccionable}
        />
      </Container>

      {!noAccionable && (
        <StickyActionBar
          items={itemsEnMesa}
          decisions={decisions}
          onSubmit={handleSubmit}
          onRejectAll={handleRejectAll}
          submitting={otpOpen}
          hayServiciosCerrados={hayServiciosCerrados}
        />
      )}

      <OTPDialog
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        token={token}
        correoSolicitante={data.solicitante?.correo}
        nombreSolicitante={data.solicitante?.nombre}
        respuestas={respuestas}
        onSuccess={handleOTPSuccess}
      />
    </Box>
  );
};

export default ProposalViewV2;
